import { Anchor, Badge, Button, Group, SimpleGrid, Stack, Table, Tabs, Text } from "@mantine/core";
import { IconCoin } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { RaiseDisputeModal } from "../components/RaiseDisputeModal";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatCard, useInfinitePage, useTableSort } from "../components/ui";
import { earningsApi, recruiterApi } from "../lib/api";
import { NAV_ICONS } from "../lib/navCopy";
import { formatDate, formatPaise, PAYOUT_STATUS_LABELS, payoutStatusColor } from "../lib/format";
import { useQueryFilters } from "../lib/useQueryFilters";
import { axioTokens } from "../theme";
import type { EarningsSummary, Page, Payout } from "../types/api";

const PAYMENT_TABS = [
  { value: "pending", label: "Pending" },
  { value: "received", label: "Received" },
  { value: "all", label: "All" },
] as const;

type TabValue = (typeof PAYMENT_TABS)[number]["value"];

const PAYMENT_TAB_DEFAULT: TabValue = "pending";
const PAYMENT_FILTER_DEFAULTS = { tab: PAYMENT_TAB_DEFAULT };
const PAYMENT_FILTER_ALLOWED = { tab: PAYMENT_TABS.map((item) => item.value) };
const PAYMENT_FILTER_OPTIONS = { allowed: PAYMENT_FILTER_ALLOWED };

export function PaymentsPage({
  summaryFn,
  payoutsFn,
  submissionBasePath = "/submissions",
  allowDispute = true,
  title = "Payments & Earnings",
  subtitle = "Track your placement payouts and payment history.",
  redirectAffiliatedRecruiters = true,
}: {
  summaryFn?: () => Promise<EarningsSummary>;
  payoutsFn?: (params: { status?: TabValue; page?: number; page_size?: number }) => Promise<Page<Payout>>;
  submissionBasePath?: string;
  allowDispute?: boolean;
  title?: string;
  subtitle?: string;
  redirectAffiliatedRecruiters?: boolean;
}) {
  const navigate = useNavigate();
  const paymentFilters = useQueryFilters(PAYMENT_FILTER_DEFAULTS, PAYMENT_FILTER_OPTIONS);
  const tab = paymentFilters.values.tab as TabValue;
  const [disputePlacementId, setDisputePlacementId] = useState<string | null>(null);
  const [disputeSubmissionId, setDisputeSubmissionId] = useState<string | null>(null);

  const profileQuery = useQuery({
    queryKey: ["my-profile"],
    queryFn: recruiterApi.myProfile,
    enabled: redirectAffiliatedRecruiters,
  });

  const affiliated = Boolean(profileQuery.data?.consultancy_id);
  const waitForProfile = redirectAffiliatedRecruiters && profileQuery.isLoading;
  const blockEarnings = redirectAffiliatedRecruiters && affiliated;

  const summaryQuery = useQuery({
    queryKey: ["earnings-summary", title],
    queryFn: summaryFn ?? earningsApi.summary,
    enabled: !waitForProfile && !blockEarnings,
  });

  const payoutsQuery = useInfinitePage(
    ["my-payouts", title, tab],
    (page, pageSize) => (payoutsFn ?? earningsApi.payouts)({ status: tab, page, page_size: pageSize }),
    { enabled: !waitForProfile && !blockEarnings },
  );

  const summary = summaryQuery.data;
  const payouts = payoutsQuery.items;
  const isLoading = waitForProfile || summaryQuery.isLoading || payoutsQuery.isLoading;

  const { sorted, sortKey, direction, toggle } = useTableSort(payouts, {
    candidate: (r) => r.placement.candidate_name?.toLowerCase() ?? "",
    job: (r) => r.placement.job_title?.toLowerCase() ?? "",
    company: (r) => r.placement.company_name?.toLowerCase() ?? "",
    amount: (r) => r.net_paise,
    status: (r) => r.status,
    date: (r) => (r.status === "paid" ? r.paid_at : r.scheduled_for),
  });

  if (redirectAffiliatedRecruiters && affiliated) {
    return <Navigate to="/" replace />;
  }

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.payments}
        title={title}
        subtitle={subtitle}
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard label="Pending" value={formatPaise(summary?.pending_paise ?? 0)} icon={IconCoin} color="money" data-testid="earnings-pending" />
        <StatCard label="Received" value={formatPaise(summary?.received_paise ?? 0)} icon={IconCoin} color="green" />
        <StatCard label="Lifetime Earnings" value={formatPaise(summary?.lifetime_paise ?? 0)} icon={IconCoin} />
      </SimpleGrid>

      <FilterBar>
        <Tabs value={tab} onChange={(value) => paymentFilters.set({ tab: value ?? PAYMENT_TAB_DEFAULT })}>
          <Tabs.List>
            {PAYMENT_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={isLoading}
        hasMore={payoutsQuery.hasMore}
        loadingMore={payoutsQuery.loadingMore}
        onLoadMore={payoutsQuery.loadMore}
        isEmpty={payouts.length === 0}
        empty={{
          icon: NAV_ICONS.payments,
          title: "No payouts yet",
          description: "No payouts in this category yet.",
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="candidate" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate
              </SortableTh>
              <SortableTh columnKey="job" sortKey={sortKey} direction={direction} onSort={toggle}>
                Job
              </SortableTh>
              <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                Company
              </SortableTh>
              <SortableTh columnKey="amount" sortKey={sortKey} direction={direction} onSort={toggle}>
                Amount
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="date" sortKey={sortKey} direction={direction} onSort={toggle}>
                Expected / Paid
              </SortableTh>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{p.placement.candidate_name}</Table.Td>
                <Table.Td>{p.placement.job_title}</Table.Td>
                <Table.Td>{p.placement.company_name}</Table.Td>
                <Table.Td>
                  <Text size="sm" className="moneyAmount">
                    {formatPaise(p.net_paise)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Badge color={payoutStatusColor(p.status)} variant="light">
                    {PAYOUT_STATUS_LABELS[p.status] ?? p.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" className="tabular">
                    {p.status === "paid" ? formatDate(p.paid_at) : formatDate(p.scheduled_for)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Anchor
                      component={Link}
                      to={`${submissionBasePath}/${p.placement.submission_id}`}
                      size="sm"
                      fw="var(--axio-font-weight-semibold)"
                      c={axioTokens.ink}
                    >
                      View
                    </Anchor>
                    {allowDispute && (
                    <Button
                      size="xs"
                      variant="outline"
                      color="orange"
                      onClick={() => {
                        setDisputePlacementId(p.placement.id);
                        setDisputeSubmissionId(p.placement.submission_id);
                      }}
                    >
                      Dispute
                    </Button>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>

      {allowDispute && (
      <RaiseDisputeModal
        opened={!!disputePlacementId}
        onClose={() => {
          setDisputePlacementId(null);
          setDisputeSubmissionId(null);
        }}
        placementId={disputePlacementId ?? undefined}
        submissionId={disputeSubmissionId ?? undefined}
        defaultType="payment"
        onCreated={(id) => navigate(`/disputes/${id}`)}
      />
      )}
    </Stack>
  );
}
